import { useMemo } from 'react';
import { Col, Row, Spinner, Alert } from 'react-bootstrap';
import Stat from '../../components/project-management/Stat';
import SalesProductBarChart from '../../components/charts/sales-management-charts/SalesProductBarChart';
import SalesJobsTable from '../../components/tables/SalesJobsTable';
import SalesDeliveryTokensTable from '../../components/tables/SalesDeliveryTokensTable';
import ExpandableActivityCard from '../../components/timelines/ExpandableActivityCard';
import { useSalesDashboardStats } from '../../features/orders/useSalesDashboard';
import { buildSalesStats } from '../../data/sales-management/salesStats';
import { useJobs } from '../../features/jobs/hooks/useJobs';
import { useFactoryActivity, useFactoryReports } from '../../features/jobs/hooks/useJob';

const SalesManagement = () => {
  const { data, isLoading, isError, error } = useSalesDashboardStats();
  const { data: jobs = [], isLoading: jobsLoading } = useJobs();
  const stats = buildSalesStats(data?.stats);

  const { data: activityData, isLoading: activityLoading } = useFactoryActivity(100);
  const { data: reportsData, isLoading: reportsLoading } = useFactoryReports(100);

  const reportTimeline = useMemo(() => {
    return Array.isArray(reportsData?.data) ? reportsData.data : [];
  }, [reportsData]);

  const totalOrdered = Number(data?.stats?.totalOrderedLength || 0);
  const totalProduced = Number(data?.stats?.totalProducedLength || 0);
  const overallPct =
    totalOrdered > 0 ? Math.round((totalProduced / totalOrdered) * 100) : 0;

  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between align-items-center">
        <Col xl={5} lg={12}>
          <div className="d-flex flex-column justify-content-center h-100">
            <h2 className="mb-2 text-1100">Sales Dashboard</h2>
            <h5 className="text-700 fw-semi-bold mb-0">
              Here's what's going on with your orders right now
            </h5>
          </div>
        </Col>

        <Col xl={7} lg={12}>
          <div className="d-flex flex-wrap gap-4 justify-content-end align-items-center">
            <div className="text-end">
              <div className="text-muted fs-10">Total Ordered</div>
              <div className="fw-bold fs-5">
                {totalOrdered.toLocaleString()} m
              </div>
            </div>
            <div className="text-end">
              <div className="text-muted fs-10">Total Produced</div>
              <div className="fw-bold fs-5">
                {totalProduced.toLocaleString()} m
              </div>
            </div>
            <div className="text-end">
              <div className="text-muted fs-10">Overall</div>
              <div className="fw-bold fs-5">{overallPct}%</div>
            </div>
          </div>
        </Col>
      </Row>

      {isError && (
        <Alert variant="danger" className="mb-3">
          Failed to load sales stats: {error?.message || 'Unknown error'}
        </Alert>
      )}

      <Row className="mb-3 gy-6">
        <Col xs={12} xxl={2}>
          <Row className="align-items-center g-3 g-xxl-0 h-100 align-content-between">
            {isLoading ? (
              <Col xs={12}>
                <div className="d-flex align-items-center justify-content-center h-100 py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading stats...</span>
                </div>
              </Col>
            ) : (
              stats.map(stat => (
                <Col
                  xs={12}
                  sm={6}
                  md={3}
                  lg={6}
                  xl={3}
                  xxl={12}
                  key={stat.title}
                >
                  <Stat stat={stat} />
                </Col>
              ))
            )}
          </Row>
        </Col>

        <Col xs={12} xxl={10}>
          <h3>Orders by Product</h3>
          <p className="text-700 lh-sm mb-3">
            Ordered vs Allocated vs Produced vs Remaining (meters)
          </p>
          <SalesProductBarChart height="380px" width="100%" />
        </Col>
      </Row>

      {/* Production Jobs Table */}
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-6 pb-3 border-y border-300 mt-3 mb-3">
        <h3 className="mb-1">Production Jobs</h3>
        <p className="text-700 mb-3">All products launched in production</p>
        <SalesJobsTable jobs={jobs} isLoading={jobsLoading} />
      </div>

      {/* Activity & Reports */}
      <div className="mx-lg-n4 mb-3 mt-3">
        <Row className="g-3">
          <Col xs={12} xl={6}>
            <ExpandableActivityCard
              title="Activity"
              seeAllTo="/apps/activity"
              subtitle="Recent activity across factory"
              reports={activityData}
              loading={activityLoading}
            />
          </Col>
          <Col xs={12} xl={6}>
            <ExpandableActivityCard
              title="Reports"
              seeAllTo="/apps/reports"
              subtitle="Production reports & completions"
              reports={reportTimeline}
              loading={reportsLoading}
            />
          </Col>
        </Row>
      </div>

      {/* Delivery Tokens Table */}
      <div className="mt-3 mb-9">
        <h3 className="mb-1">Order Delivery Tracking</h3>
        <p className="text-700 mb-3">
          Final-phase tokens linked to orders — live production status
        </p>
        <SalesDeliveryTokensTable />
      </div>
    </>
  );
};

export default SalesManagement;
